import {
  Currency,
  CustomParameters,
  Duration,
  DurationUnit,
  PaywallFeature,
  PaywallSession,
  Plan,
  StoredCreditCard,
  Subscription,
  SubscriptionStatus,
  Voucher,
} from '@accedo/one-plugin-sdk';
import { t } from 'i18next';
import { useLocation } from 'react-router-dom';

import { axios, userService } from '#/client';
import { useConfig } from '#/hooks';

import { CardStatus } from './models';

// TODO: improve binding context handling everywhere

export const translatePrice = (
  price: string | number,
  currency: Currency,
  duration?: Duration
) => {
  // TODO: handle one-off purchases in the plugin and Plan interface properly, temporary workaround for Rural's use-case to avoid changing plugins
  // Only return the /{unit} suffix when the contract length is per-unit (per month, per year)
  if (duration?.count === 1) {
    return t(`web.iap.price.periodical.${duration.unit.toLowerCase()}`, {
      purchase: { price, currency: translateCurrency(currency) },
    });
  }

  return t(`web.iap.price.oneOff`, {
    purchase: { price, currency: translateCurrency(currency) },
  });
};

export const getPickerItemsFromPlans = (plans: Plan[]) =>
  plans.map(
    ({ id, title, description, price, currency, duration, nextPayment }) => ({
      id,
      title,
      subtitle: translateDescription(description, price, currency, nextPayment),
      detail: translatePrice(price, currency, duration),
    })
  );

export const translateDescription = (
  description: string,
  price: string,
  currency: Currency,
  date: Date
) => {
  return tDouble(description, {
    date,
    purchase: {
      price,
      currency: translateCurrency(currency),
    },
    // TODO: backward compatibility. remove later.
    price,
    currency: translateCurrency(currency),
  });
};

export const translateDiscount = (currency: Currency, voucher: Voucher) => {
  const {
    isPercent,
    discountAmount,
    duration: { count, unit },
  } = voucher;
  const discountKey = isPercent ? 'discountPercentage' : 'discountAbsolute';
  const unitKey = unit === DurationUnit.Forever ? 'forever' : `first${unit}`;

  return t(`web.iap.voucher.${discountKey}.${unitKey}`, {
    count,
    purchase: {
      voucher: {
        [discountKey]: discountAmount,
      },
      currency: isPercent ? '' : translateCurrency(currency),
    },
  });
};

export function isPromotionalSubscription(subscription?: Subscription) {
  return !!subscription?.customParameters?.['promotion'];
}

export const translateSubscriptionStatus = (subscription: Subscription) => {
  const { expiration, scheduledMove, nextPayment, nextPrice, status } =
    subscription;
  const isPromotionalSub = isPromotionalSubscription(subscription);

  if (
    !isPromotionalSub &&
    (status === SubscriptionStatus.PendingCancellation ||
      status === SubscriptionStatus.Canceled)
  ) {
    return t('iap.subscription.status.canceled', {
      date: expiration,
    });
  }

  if (status === SubscriptionStatus.Unknown) {
    return t('web.iap.subscription.status.unknown');
  }

  if (scheduledMove) {
    return tDouble('web.iap.subscription.status.moved', {
      date: scheduledMove.date,
      purchase: {
        next: {
          ...scheduledMove.nextPlan,
          currency: translateCurrency(scheduledMove.nextPlan.currency),
        },
      },
    });
  }

  if (isPromotionalSub && !scheduledMove) {
    return t('web.promos.subscription.status.cancelled', { date: nextPayment });
  }

  if (status === SubscriptionStatus.TimeLimited) {
    return t('web.iap.subscription.status.timeLimited', { date: expiration });
  }

  if (status === SubscriptionStatus.PendingActivation) {
    return t('web.iap.subscription.status.pendingActivation');
  }

  if (nextPrice === '0' && !nextPayment) {
    return t('web.iap.subscription.status.free');
  }

  return t('web.iap.subscription.status.recurring', { date: nextPayment });
};

export const translatePaymentMethodStatus = (
  status: CardStatus,
  card?: StoredCreditCard
) => {
  return t(
    status === CardStatus.Valid
      ? `iap.paymentMethod.card.type.debit`
      : `iap.paymentMethod.card.status.${status}`,
    {
      purchase: {
        ...(card && {
          card: {
            type: card.cardType,
            endingNumbers: card.cardNumber.slice(card.cardNumber.length - 4),
          },
        }),
      },
    }
  );
};

export const getPaywallSession = (sessionEndpoint: string) => {
  return axios.request<PaywallSession>({
    url: sessionEndpoint,
    method: 'GET',
  });
};

const translateCurrency = (currency: Currency) => {
  return t(`web.iap.currency.${currency?.toLowerCase()}`);
};

/**
 * This special function tries to deal with an interesting edge case in our translations.
 * For some reason on our DEV/PROD environments interpolation is working only for the first variable, when there
 * are two next to each other. This fix is just running t function twice, so that interpolation will work as
 * expected. Funny thing - it works fine on Storybook configuration and I did an investigation what are
 * the differences between those 2 configurations and found nothing :( Problematic texts comes from 3rd party endpoints
 * This function is expected to be removed once the root cause is found and resolved
 **/
function tDouble(text: string, params?: object) {
  return t(t(text, params), params);
}

/**
 * Take the `vendorTag` out of the URL, verify it based on the paywall config, and return it as a `CustomParameters` object.
 * @returns {CustomParameters|undefined} Object with `vendorTag` in it, or undefined.
 */
export const useSubscriptionParameters = (): CustomParameters | undefined => {
  const {
    features: { paywall = [] },
  } = useConfig();
  const location = useLocation();

  // disabled paywalls are alredy filtered out. if we have multiple enabled, use the first one.
  const supportedVendorTags = paywall[0]?.properties?.vendorTags ?? [];
  const vendorTag = new URLSearchParams(location.search).get('vendorTag') ?? '';

  if (supportedVendorTags.includes(vendorTag)) {
    return { vendorTag };
  }

  if (vendorTag) {
    console.debug('Ignored invalid vendorTag parameter', {
      configured: supportedVendorTags,
      actual: vendorTag,
    });
  }

  return undefined;
};

export const fetchOauthToken = () =>
  userService
    .getOauthToken()
    .then((r) => r.data.oauth?.accessToken)
    .catch((err) => {
      console.error("Can't fetch OAuth token", err);

      return null;
    });

/**
 * Checks a list of features to see if they are supported.
 *
 * @returns A list of boolean values matching the support for each given feature
 */
export const supportsFeatures = (
  featuresToCheck: PaywallFeature[],
  supportedFeatures: PaywallFeature[]
): boolean[] =>
  featuresToCheck.map((feature) => supportedFeatures?.includes(feature));
